type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

interface ArticleAnalyticsData {
  title?: string;
  subtitle?: string;
  body?: string;
  sourceUrl?: string;
}

interface GenerationSettings {
  redditUrl: string;
  tone: string;
  length: string;
  language: string;
  customPrompt?: string;
}

declare global {
  interface Window {
    gtag?: (command: "event", eventName: string, params?: AnalyticsParams) => void;
  }
}

const MAX_PARAM_LENGTH = 500;
let pageOpenTracked = false;

function trimForAnalytics(value: string) {
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized.length > MAX_PARAM_LENGTH
    ? `${normalized.slice(0, MAX_PARAM_LENGTH - 1)}...`
    : normalized;
}

function normalizeUrlForAnalytics(value: string) {
  try {
    const parsedUrl = new URL(value);
    parsedUrl.search = "";
    parsedUrl.hash = "";
    return parsedUrl.toString();
  } catch {
    return value.trim();
  }
}

function hashText(value: string) {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash).toString(36);
}

function cleanParams(params: AnalyticsParams) {
  return Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value !== null && value !== undefined && value !== "")
      .map(([key, value]) => [
        key,
        typeof value === "string" ? trimForAnalytics(value) : value,
      ])
  ) as AnalyticsParams;
}

function trackEvent(eventName: string, params: AnalyticsParams = {}) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  window.gtag("event", eventName, cleanParams(params));
}

function settingsParams(settings: GenerationSettings) {
  return {
    reddit_url: normalizeUrlForAnalytics(settings.redditUrl),
    tone: settings.tone,
    article_length_setting: settings.length,
    language: settings.language,
    custom_prompt_used: Boolean(settings.customPrompt?.trim()),
    custom_prompt_length: settings.customPrompt?.trim().length || 0,
  };
}

export function trackPageOpened() {
  if (pageOpenTracked) {
    return;
  }

  pageOpenTracked = true;

  trackEvent("page_opened", {
    page_title: document.title,
    page_path: window.location.pathname,
    page_location: window.location.href,
  });
}

export function trackRedditUrlSubmitted(settings: GenerationSettings) {
  trackEvent("reddit_url_submitted", settingsParams(settings));
}

export function trackArticleGenerated(article: ArticleAnalyticsData, settings: GenerationSettings) {
  const body = article.body || "";

  trackEvent("article_generated", {
    ...settingsParams(settings),
    source_url: article.sourceUrl ? normalizeUrlForAnalytics(article.sourceUrl) : undefined,
    article_title: article.title,
    article_subtitle: article.subtitle,
    article_body_excerpt: body.slice(0, MAX_PARAM_LENGTH),
    article_body_length: body.length,
    article_body_hash: body ? hashText(body) : undefined,
  });
}

export function trackArticleGenerationFailed(settings: GenerationSettings, errorMessage: string) {
  trackEvent("article_generation_failed", {
    ...settingsParams(settings),
    error_message: errorMessage,
  });
}

